import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { Spa } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";

export const CreationActions = ({
  conversationId,
  refreshKey,
  onSelectClass,
}) => {
  const api = useApiClient();
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState(null);
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [conversationId],
  );
  useEffect(() => {
    let active = true;
    let timer;
    setActions([]);
    if (!conversationId) return;
    const load = async () => {
      try {
        const result = await api.assistant.getActions(conversationId);
        if (!active) return;
        setError(null);
        setActions(result || []);
        if (result?.some((a) => ["queued", "generating"].includes(a.status)))
          timer = setTimeout(load, 3000);
      } catch {
        if (active)
          setError("Unable to load Bud’s draft operations. Please refresh.");
      }
    };
    load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [conversationId, refreshKey, refresh]);
  const perform = async (action, operation) => {
    const attempt = generation.current;
    setBusy(action.id);
    try {
      const result = await api.assistant.performAction(
        action.id,
        operation,
        action.revision,
      );
      if (attempt !== generation.current) return;
      enqueueSnackbar(result.error || "Bud operation updated", {
        variant: result.success ? "success" : "error",
      });
      if (result.action)
        setActions((items) =>
          items.map((a) => (a.id === action.id ? result.action : a)),
        );
      setRefresh((n) => n + 1);
    } catch {
      if (attempt !== generation.current) return;
      enqueueSnackbar(
        "Unable to update this operation. Refresh its review before retrying.",
        { variant: "error" },
      );
    } finally {
      if (attempt === generation.current) setBusy(null);
    }
  };
  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {onSelectClass &&
        actions.some(
          (action) =>
            action.status === "needs_details" &&
            action.specification.needs_class_selection,
        ) && (
          <Button
            variant="outlined"
            onClick={() =>
              onSelectClass(
                actions.find(
                  (action) => action.specification.needs_class_selection,
                ).kind,
              )
            }
          >
            Select class
          </Button>
        )}
      {error && (
        <Stack>
          <Typography color="error">{error}</Typography>
          <Button onClick={() => setRefresh((n) => n + 1)}>
            Refresh operations
          </Button>
        </Stack>
      )}
      {actions
        .filter((a) => a.status !== "needs_details")
        .map((action) => {
          const spec = action.specification;
          const draft = action.review?.draft;
          return (
            <Card key={action.id} variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="h6">
                    {draft?.name ||
                      spec.name ||
                      "Add assignment to more classes"}
                  </Typography>
                  <Chip
                    label={action.status}
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                  />
                  <Typography variant="body2">{spec.topic}</Typography>
                  <Typography variant="body2">
                    Due:{" "}
                    {draft?.due_date ||
                      spec.due_date ||
                      action.review?.source?.due_date}
                    . Class: {action.className || spec.class_id}
                  </Typography>
                  {action.kind === "quiz" && (
                    <Typography variant="body2">
                      {spec.number_of_long} written · {spec.number_of_mcq}{" "}
                      multiple choice · {spec.number_of_true_false} true/false
                    </Typography>
                  )}
                  {spec.questions?.map((question, index) => (
                    <Stack key={index} spacing={0.5}>
                      <Typography variant="subtitle2">
                        Question {index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {question.topic}
                      </Typography>
                      <Typography variant="body2">
                        {Object.entries(question.ao_config)
                          .map(
                            ([id, marks]) =>
                              `${action.objectiveLabels?.[id] || "Objective"}: ${marks} marks`,
                          )
                          .join(" · ")}
                      </Typography>
                    </Stack>
                  ))}
                  {action.kind === "duplicate" && (
                    <Typography>
                      Additional classes:{" "}
                      {(action.targetClasses || [])
                        .map((sc) => sc.name)
                        .join(", ")}
                      . Copies will be published. Existing matches shown in this
                      review will be linked and inherit the original
                      preferences.
                    </Typography>
                  )}
                  {action.review?.existing?.map((item) => (
                    <Typography key={item.id} variant="body2">
                      Existing assignment {item.id} in class{" "}
                      {item.school_class_id}
                    </Typography>
                  ))}
                  {draft?.questions.map((q) => (
                    <Stack key={q.id} spacing={0.5}>
                      <Typography variant="subtitle2">
                        {q.text}
                        {q.complete ? "" : " — needs marking criteria/options"}
                      </Typography>
                      {q.options?.map((option, i) => (
                        <Typography variant="body2" key={i}>
                          {option.option__text}
                          {option.is_correct ? " (correct)" : ""}
                        </Typography>
                      ))}
                      {q.guidance && (
                        <Typography variant="body2">
                          Marking guidance: {q.guidance}
                        </Typography>
                      )}
                      {q.objectives?.map((objective, i) => (
                        <Typography variant="body2" key={i}>
                          {objective.label || "Assessment objective"}:{" "}
                          {objective.marks} marks.{" "}
                          {objective.additional_instructions}
                        </Typography>
                      ))}
                    </Stack>
                  ))}
                  {action.error && (
                    <Typography color="error">{action.error}</Typography>
                  )}
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {action.assignmentId && (
                      <Button
                        variant={
                          action.status === "ready" ? "contained" : "text"
                        }
                        component={Link}
                        to={`/assignments/view/${action.assignmentId}`}
                      >
                        Go to Assignment
                      </Button>
                    )}
                    {action.quizGroupId && (
                      <Button
                        variant={
                          action.status === "ready" ? "contained" : "text"
                        }
                        component={Link}
                        to={`/quizzes/view/${action.quizGroupId}`}
                      >
                        Go to Quiz
                      </Button>
                    )}
                    <Button
                      disabled={busy === action.id}
                      onClick={() => perform(action, "review")}
                    >
                      Refresh review
                    </Button>
                    {action.status === "proposed" && (
                      <Button
                        variant="contained"
                        startIcon={
                          action.kind === "duplicate" ? undefined : <Spa />
                        }
                        disabled={busy === action.id}
                        onClick={() =>
                          perform(
                            action,
                            action.kind === "duplicate"
                              ? "duplicate"
                              : "generate",
                          )
                        }
                      >
                        {action.kind === "duplicate"
                          ? "Confirm and add classes"
                          : "Generate with Bud"}
                      </Button>
                    )}
                    {action.status === "failed" && (
                      <Button
                        disabled={busy === action.id}
                        onClick={() => perform(action, "retry")}
                      >
                        Retry generation
                      </Button>
                    )}
                    {[
                      "proposed",
                      "queued",
                      "generating",
                      "ready",
                      "failed",
                    ].includes(action.status) && (
                      <Button
                        disabled={busy === action.id}
                        onClick={() => perform(action, "cancel")}
                      >
                        Cancel
                      </Button>
                    )}
                  </Stack>
                  {action.status === "published" && (
                    <Typography variant="body2">
                      Ready to share from the assessment page. For an
                      assignment, you can now ask Bud to add it to more classes.
                    </Typography>
                  )}
                  {action.results && (
                    <Typography>
                      Created {action.results.created}; linked{" "}
                      {action.results.linked}.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      {!error &&
        actions.some((action) =>
          ["queued", "generating"].includes(action.status),
        ) && (
          <Stack role="status" aria-live="polite" sx={{ py: 1 }}>
            <GeneralLoading message="Generating…" direction="row" size={28} />
          </Stack>
        )}
    </Stack>
  );
};
